import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, DefaultButton, TextField, Separator, TooltipHost, Icon } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { type ShockProfile, type ShockProfileUpdate } from "../../api";
import { createUuid } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { doubleBindString } from "../../utils/double-bind";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { GtomG, mGtoG } from "../../utils/unit-conversion";
import { FloatInput } from "../atoms/float-input";

export interface FormUpdateShockProfileProps extends FormUpdateProps<ShockProfile> {}

import { store } from "../../store";

@observer
export class FormUpdateShockProfile extends Component<FormUpdateShockProfileProps> {
    // These fields are supposed to be displayed as `g` in the frontend, even
    // though we work with `mg` under the hood.
    @observable public accessor light: number | undefined;
    @observable public accessor medium: number | undefined;
    @observable public accessor severe: number | undefined;

    // Duration unit is ms
    @observable public accessor duration: number | undefined;

    @observable public accessor xAxisScale: number | undefined;
    @observable public accessor yAxisScale: number | undefined;
    @observable public accessor zAxisScale: number | undefined;

    private validationId = createUuid();

    constructor(props: FormUpdateShockProfileProps) {
        super(props);
        makeObservable(this);
        store.repositoryShockProfiles.validation.update.initializeModel(
            this.validationId,
            this.shockProfileUpdate,
            this.props.id,
        );
    }

    private triggerValidation(): void {
        store.repositoryShockProfiles.validation.update.updateModel(
            this.validationId,
            this.shockProfileUpdate,
        );
    }

    public componentWillUnmount(): void {
        store.repositoryShockProfiles.discardMutableCopy(
            FormUpdateShockProfile.name,
            this.props.id,
        );
    }

    @computed private get shockProfile(): ShockProfile | undefined {
        return store.repositoryShockProfiles.mutableCopyById(
            FormUpdateShockProfile.name,
            this.props.id,
        );
    }

    @computed private get shockProfileUpdate(): ShockProfileUpdate {
        const update = pick(["label"], this.shockProfile) as ShockProfileUpdate;

        update["light"] = GtomG(this.light);
        update["medium"] = GtomG(this.medium);
        update["severe"] = GtomG(this.severe);
        update["duration"] = this.duration;
        update["xAxisScale"] = this.xAxisScale;
        update["yAxisScale"] = this.yAxisScale;
        update["zAxisScale"] = this.zAxisScale;

        return update;
    }

    @action.bound private setLight(value?: number): void {
        this.light = value;
        this.triggerValidation();
    }

    @action.bound private setMedium(value?: number): void {
        this.medium = value;
        this.triggerValidation();
    }

    @action.bound private setSevere(value?: number): void {
        this.severe = value;
        this.triggerValidation();
    }

    @action.bound private setDuration(value?: number): void {
        this.duration = value;
        this.triggerValidation();
    }

    @action.bound
    private async updateShockProfile(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const shockProfile = await store.repositoryShockProfiles.update(
            this.props.id,
            this.shockProfileUpdate,
        );
        this.props.onUpdate(shockProfile);
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateShockProfile.submit.text")}
                validation={store.repositoryShockProfiles.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateShockProfile}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formCreateShockProfile.label.label")}
                        required
                        {...doubleBindString(this.shockProfile!, "label", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.light.label")}
                        precision={3}
                        defaultValue={mGtoG(this.shockProfile!.light)}
                        onChange={this.setLight}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "light",
                            ),
                            (p) => store.i18n.formatFloat(mGtoG(String(p)), 3),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.medium.label")}
                        precision={3}
                        defaultValue={mGtoG(this.shockProfile!.medium)}
                        onChange={this.setMedium}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "medium",
                            ),
                            (p) => store.i18n.formatFloat(mGtoG(String(p)), 3),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.severe.label")}
                        precision={3}
                        defaultValue={mGtoG(this.shockProfile!.severe)}
                        onChange={this.setSevere}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "severe",
                            ),
                            (p) => store.i18n.formatFloat(mGtoG(String(p)), 3),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.duration.label")}
                        precision={0}
                        defaultValue={this.shockProfile!.duration}
                        onChange={this.setDuration}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "duration",
                            ),
                            (p) => p,
                        )}
                    />
                    <Separator />
                    <div style={{ position: "relative" }}>
                        <TooltipHost
                            content={store.i18n.t("formCreateShockProfile.axesScaling.tooltip")}
                            calloutProps={{ gapSpace: 0, target: "#axis-icon" }}
                        >
                            <Icon
                                id="axis-icon"
                                styles={{ root: { position: "absolute", right: 0, zIndex: 1 } }}
                                iconName="Info"
                            />
                        </TooltipHost>
                    </div>
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.xAxisScale.label")}
                        precision={6}
                        defaultValue={this.shockProfile!.xAxisScale}
                        onChange={(val) => {
                            this.xAxisScale = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "xAxisScale",
                            ),
                            (p) => p,
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.yAxisScale.label")}
                        precision={6}
                        defaultValue={this.shockProfile!.yAxisScale}
                        onChange={(val) => {
                            this.yAxisScale = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "yAxisScale",
                            ),
                            (p) => p,
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.zAxisScale.label")}
                        precision={6}
                        defaultValue={this.shockProfile!.zAxisScale}
                        onChange={(val) => {
                            this.zAxisScale = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.update.getFieldValidationState(
                                this.validationId,
                                "zAxisScale",
                            ),
                            (p) => p,
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateShockProfile.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
